import React, { useState, useMemo } from 'react';
import { usePredictionPerformance } from '../../../helpers/api/prediction_performance/prediction-performance';
import { LoadingSpinner } from '../../ComponnentsUI/common/LoadingSpinner';
import { ErrorDisplay } from '../../ComponnentsUI/common/ErrorDisplay';
import { Card } from '../../ComponnentsUI/common/Card';
import { Select } from '../../ComponnentsUI/common/Select';

const PredictionPerformancePage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<'REALTIME' | 'BATCH' | 'BLIND_SWEEP' | 'ALL'>('ALL');
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const limit = 20;

  const recordsParams = useMemo(() => ({
    prediction_type: selectedType !== 'ALL' ? selectedType : undefined,
    limit,
    offset: (page - 1) * limit,
    days,
  }), [selectedType, page, limit, days]);

  const { data: recordsData, isLoading: recordsLoading, error: recordsError } = usePredictionPerformance(recordsParams);

  const records = recordsData?.records || [];
  const pagination = recordsData?.pagination;

  const formatTime = (ms?: number) => {
    if (ms === undefined || ms === null) return 'N/A';
    if (ms < 1000) return `${ms.toFixed(2)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'REALTIME': return '#3B82F6';
      case 'BATCH': return '#10B981';
      case 'BLIND_SWEEP': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  return (
    <div className="bg-card-color-200 p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-title-page-color-100">Prediction Performance Metrics</h1>
        <p className="text-sm text-gray-500 mt-1">Track and analyze prediction processing times</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Type:</label>
          <Select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value as any);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            options={[
              { value: 'ALL', label: 'All Types' },
              { value: 'REALTIME', label: 'Realtime' },
              { value: 'BATCH', label: 'Batch' },
              { value: 'BLIND_SWEEP', label: 'Blind Sweep' },
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Days:</label>
          <Select
            value={String(days)}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            options={[
              { value: '1', label: 'Last 24 hours' },
              { value: '7', label: 'Last 7 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
              { value: '365', label: 'Last year' },
            ]}
          />
        </div>
      </div>

      {/* Records Table */}
      <Card title="Recent Performance Records">
        {recordsLoading ? (
          <LoadingSpinner />
        ) : recordsError ? (
          <ErrorDisplay message={recordsError?.message || 'Failed to load records'} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Min Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Max Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avg Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Frames
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="px-2 py-1 text-xs font-semibold rounded-full text-white"
                          style={{ backgroundColor: getTypeColor(record.prediction_type) }}
                        >
                          {record.prediction_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(record.min_processing_time_ms)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(record.max_processing_time_ms)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(record.avg_processing_time_ms)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.frame_count || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(record.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total > limit && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} records
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!pagination.has_more}
                    className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default PredictionPerformancePage;

